import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/", key: "home" },
  { label: "电影", to: "/category/movie", key: "movie" },
  { label: "电视剧", to: "/category/tv", key: "tv" },
  { label: "综艺", to: "/category/variety", key: "variety" },
  { label: "搜索", to: "/search", key: "search" },
  { label: "关于", to: "/about", key: "about" },
];

export default function SecondaryNav({ active }: { active?: string }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  return (
    <header
      className={`sticky top-0 z-40 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-background-200"
          : "bg-primary-900 border-primary-800"
      }`}
    >
      <div className="w-full px-4 md:px-6 xl:px-10">
        <div className="flex items-center justify-between h-16 md:h-[68px] gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
            <span className="w-9 h-9 rounded-lg bg-accent-500 text-background-50 flex items-center justify-center text-lg font-bold font-heading">
              <i className="ri-play-fill"></i>
            </span>
            <span
              className={`font-heading font-bold text-[18px] tracking-tight transition-colors ${
                scrolled ? "text-foreground-950" : "text-background-50"
              }`}
            >
              91<span className="text-accent-500">视频</span>网站
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = active === item.key;
              return (
                <Link
                  key={item.key}
                  to={item.to}
                  className={`px-3.5 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive
                      ? scrolled
                        ? "bg-secondary-300 text-foreground-950"
                        : "bg-secondary-500 text-foreground-950"
                      : scrolled
                        ? "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                        : "text-background-100 hover:text-secondary-300"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={submitSearch} className="hidden md:flex items-center relative">
              <i
                className={`ri-search-line absolute left-3 text-sm transition-colors ${
                  scrolled ? "text-foreground-400" : "text-background-300"
                }`}
              ></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视名称"
                aria-label="搜索影视名称"
                className={`w-44 lg:w-56 h-9 pl-9 pr-3 text-sm rounded-full outline-none border transition-colors ${
                  scrolled
                    ? "bg-background-100 border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400"
                    : "bg-background-50/10 border-background-50/25 text-background-50 placeholder:text-background-300 focus:border-secondary-400"
                }`}
              />
            </form>
            <Link
              to="/about"
              className={`hidden sm:flex items-center h-9 px-4 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                scrolled
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                  : "bg-accent-500 text-background-50 hover:bg-accent-600"
              }`}
            >
              <i className="ri-chat-smile-2-line mr-1.5"></i>
              反馈
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="打开导航菜单"
              className={`lg:hidden w-9 h-9 rounded-md flex items-center justify-center transition-colors ${
                scrolled ? "text-foreground-800 hover:bg-background-100" : "text-background-50 hover:bg-background-50/10"
              }`}
            >
              <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 animate-fade-in">
          <form onSubmit={submitSearch} className="px-4 pt-3 pb-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视名称"
                aria-label="搜索影视名称"
                className="flex-1 h-10 px-3 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
              />
              <button
                type="submit"
                className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap"
              >
                搜索
              </button>
            </div>
          </form>
          <nav className="flex flex-col px-2 pb-3">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                className={`flex items-center justify-between px-3 h-11 rounded-md text-sm ${
                  active === item.key
                    ? "bg-secondary-100 text-foreground-950 font-medium"
                    : "text-foreground-700"
                }`}
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}